import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { formatLinkBlockToUrl } from '$utilities/data';
import { isLocalURL } from '$utilities/browser.js';
import Util from '$utilities';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Footer = (props) => {
    const {
        address,
        addressHeader,
        clientList,
        copy,
        copyright,
        footerLinksCol1,
        footerLinksCol2,
        footerLinksHeader,
        mailchimpId,
        newsletterHeader,
        newsletterSignupPlaceholder,
        newsletterSubmitLabel,
    } = props;

    // state
    const [errorMessage, setErrorMessage] = useState();
    const [message, setMessage] = useState();

    // hooks
    const {
        register,
        handleSubmit,
        reset
    } = useForm();

    // methods
    const onSubmit = async (e) => {
        setErrorMessage(null);
        setMessage(null);

        try {
            // send "add user to mailing audience / list" request to our api endpoint
            const response = await fetch('/api/mailchimp/subscribeOrUpdate', {
                body: JSON.stringify({
                    'email_address': e.email,
                    audienceListId: mailchimpId,
                    status: 'pending'
                }),
                headers: {
                    'Content-type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                method: 'POST'
            });
            if (response.status === 200) {
                const body = await response.json();
                if (body.redirectUri) {
                    const url = new URL(body.redirectUri);
                    window.location = url.href;
                }
                reset();
                setMessage('Thank you for subscribing! You\'ll receive a confirmation email shortly.');
            } else {
                const data = await response.json();
                throw new Error(data?.message);
            }

        } catch (err) {
            setErrorMessage(err.message || err.title);
        }
    };

    // render
    return (
        <footer className='z-10'>
            {/* Top Section */}
            <div
                className='layout-container pt-20 pb-40 sm:py-60 lg:py-80 mb-1 rounded bg-light-grey'>
                <div className='flex flex-col md:flex-row gap-26 sm:gap-40 md:gap-0'>
                    {/* Left Col */}
                    <div className='px-layout-item w-full sm:w-2/5 md:w-3/7 lg:w-4/9'>
                        <div className='w-full md:w-2/3 lg:w-[70%]'>
                            <h2 className='type-style-4'>
                                {copy}
                            </h2>
                        </div>
                    </div>

                    {/* Right Col */}
                    <div className='px-layout-item w-full sm:w-4/5 md:w-4/7 lg:w-5/9'>
                        <div className='w-full'>
                            <p className='type-style-4'>
                                {clientList}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lower Section */}
            <div className='layout-container py-20 sm:py-40 md:py-60 lg:pb-50 lg:pt-60 rounded bg-light-grey'>
                <div className='flex flex-col sm:flex-row'>
                    {/* Left Col */}
                    <div className='flex flex-col justify-between px-layout-item w-full sm:w-3/5 md:w-3/7 lg:w-4/9 gap-50'>
                        {/* Address */}
                        <div className='flex flex-col gap-26 lg:gap-34'>
                            <div>
                                <div className='w-full md:w-2/3 lg:w-[70%]'>
                                    <p className='type-style-4'>
                                        {addressHeader}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <div
                                    className='type-style-4'
                                    dangerouslySetInnerHTML={{ __html: address }} />
                            </div>
                        </div>

                        {/* Desktop Logo */}
                        <div className='hidden sm:block w-full type-style-hero-sm'>
                            <p
                                className='pointer-events-none select-none leading-[0.78]'
                                inert='true'>
                                BureauVA
                            </p>
                        </div>
                    </div>

                    <div className='px-layout-item w-full sm:w-2/5 md:w-4/7 lg:w-5/9 flex flex-col justify-between'>
                        <div>
                            {/* Newsletter */}
                            <form
                                className='flex flex-col gap-34 mt-34 sm:mt-0'
                                onSubmit={handleSubmit(onSubmit)}>
                                <label
                                    className='type-style-4'
                                    htmlFor='frm-email'>
                                    {newsletterHeader}
                                </label>

                                <div className='flex flex-row'>
                                    <input
                                        required
                                        className='border-b w-4/5 type-style-4 pb-10 bg-light-grey'
                                        id='frm-email'
                                        name='email'
                                        placeholder={newsletterSignupPlaceholder}
                                        type='text'
                                        {...register('email', { required: true })} />

                                    {(errorMessage || message) && (
                                        <div className='flex flex-wrap justify-between items-center w-full px-10 sm:px-20'>
                                            {errorMessage &&
                                                <div className='flex items-center type-style-4 mr-20 '>
                                                    {errorMessage}
                                                </div>
                                            }

                                            {message &&
                                                <div className='flex items-center type-style-4 mr-20 '>
                                                    {message}
                                                </div>
                                            }
                                        </div>
                                    )}

                                    <input
                                        className='ml-20 type-style-4 cursor-pointer'
                                        type='submit'
                                        value={newsletterSubmitLabel} />
                                </div>
                            </form>

                            {/* Footer Links */}
                            <p className='type-style-4 mt-55 sm:mt-52 md:mt-50 lg:mt-64'>
                                {footerLinksHeader}
                            </p>

                            <div className='flex flex-row lg:w-3/5 mt-26'>
                                {footerLinksCol1?.length > 0 && (
                                    <ul className='flex flex-col w-1/4'>
                                        {footerLinksCol1?.map((item, i) => {
                                            const href = formatLinkBlockToUrl(item?.link);
                                            const isLocal = isLocalURL(href);

                                            return (
                                                <li key={i}>
                                                    <a
                                                        className='type-style-4'
                                                        href={href}
                                                        rel={isLocal ? null : 'noopener noreferrer'}
                                                        target={isLocal ? null : '_blank'}>
                                                        {item?.label}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {footerLinksCol2?.length > 0 && (
                                    <ul className='flex flex-col w-1/4'>
                                        {footerLinksCol2?.map((item, i) => {
                                            const href = formatLinkBlockToUrl(item?.link);
                                            const isLocal = isLocalURL(href);

                                            return (
                                                <li key={i}>
                                                    <a
                                                        className='type-style-4'
                                                        href={href}
                                                        rel={isLocal ? null : 'noopener noreferrer'}
                                                        target={isLocal ? null : '_blank'}>
                                                        {item?.label}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Mobile Logo */}
                        <div className='block sm:hidden w-full mt-52 mb-24'>
                            <p
                                className='type-style-hero pointer-events-none select-none'
                                inert='true'>
                                BureauVA
                            </p>
                        </div>

                        <p className='type-style-4 sm:mt-80 lg:mt-20'>
                            <span>&copy; {copyright}</span>
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Footer.propTypes = {
    address: PropTypes.string,
    addressHeader: PropTypes.string,
    clientList: PropTypes.string,
    copy: PropTypes.string,
    copyright: PropTypes.string,
    footerLinksCol1: PropTypes.array,
    footerLinksCol2: PropTypes.array,
    footerLinksHeader: PropTypes.string,
    mailchimpId: PropTypes.string,
    newsletterHeader: PropTypes.string,
    newsletterSignupPlaceholder: PropTypes.string,
    newsletterSubmitLabel: PropTypes.string,
};

export default Footer;
